import { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Medal, Trophy, MagnifyingGlass, UserPlus, Broadcast, CheckCircle, Users } from "@phosphor-icons/react";
import MultiClubSelect from "@/components/MultiClubSelect";
import BestPairs from "@/components/BestPairs";

// FEMEPA = Federación Mexicana de Pádel - oficial categorías y géneros
const FEMEPA_CATS = ["1", "2", "3", "4", "5", "6", "open"];
const FEMEPA_GENDERS = [
  { id: "varonil", label: "Varonil", color: "bg-blue-500/10 text-blue-300 border-blue-500/30" },
  { id: "femenil", label: "Femenil", color: "bg-pink-500/10 text-pink-300 border-pink-500/30" },
  { id: "mixta", label: "Mixta", color: "bg-purple-500/10 text-purple-300 border-purple-500/30" },
];

const FEMEPA_KINDS = (() => {
  const out = {};
  for (const cat of FEMEPA_CATS) {
    for (const g of FEMEPA_GENDERS) {
      const kind = `femepa_${cat}_${g.id}`;
      const label = `FEMEPA ${cat === "open" ? "OPEN" : cat} ${g.label}`;
      out[kind] = { label, color: g.color, femepa: true, category: cat, gender: g.id };
    }
  }
  return out;
})();

const KIND_LABELS = {
  ...FEMEPA_KINDS,
  veteranos_a: { label: "Veteranos A", color: "bg-amber-500/10 text-amber-400 border-amber-500/30" },
  veteranos_b: { label: "Veteranos B", color: "bg-orange-500/10 text-orange-400 border-orange-500/30" },
  sexta_open: { label: "Sexta-Open", color: "bg-[#CCFF00]/10 text-[#CCFF00] border-[#CCFF00]/30" },
  general: { label: "General", color: "bg-white/5 text-gray-300 border-white/10" },
};

const EMPTY = { name: "", description: "", kind: "general", start_date: "", end_date: "", club_ids: [] };

export default function Leagues() {
  const { user, permissions } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const canCreateLeagues = permissions?.canCreateLeagues;
  // Edit/Delete: admin, directivo of club, or creator (jugador/profesor).
  const canManageLeague = (lg) => {
    if (!user || !lg) return false;
    if (user.role === "admin") return true;
    if (user.role === "directivo") {
      const lgClubIds = new Set([...(lg.club_ids || []), ...(lg.club_id ? [lg.club_id] : [])]);
      const uClubIds = new Set([...(user.club_ids || []), ...(user.club_id ? [user.club_id] : [])]);
      for (const cid of uClubIds) if (lgClubIds.has(cid)) return true;
    }
    if (lg.created_by && lg.created_by === user.id) return true;
    return false;
  };
  const [leagues, setLeagues] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [rankingFor, setRankingFor] = useState(null);
  const [rankingData, setRankingData] = useState(null);
  const [rankingCategory, setRankingCategory] = useState("all");
  const [bracketFor, setBracketFor] = useState(null);
  const [bracketData, setBracketData] = useState(null);
  const [search, setSearch] = useState("");
  const [genderFilter, setGenderFilter] = useState("all");
  const [enrollFor, setEnrollFor] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [marcadoresFor, setMarcadoresFor] = useState(null);
  const [marcadoresData, setMarcadoresData] = useState([]);
  const [linkFor, setLinkFor] = useState(null);   // admin tournament-link dialog
  const [linkData, setLinkData] = useState(null); // {linked: [], unlinked: []}

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/leagues"),
      api.get("/clubs"),
    ])
      .then(([lg, c]) => { setLeagues(lg.data || []); setClubs(c.data || []); })
      .catch(() => toast.error("Error"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Deep-link: /dashboard/ligas?league=<id> — auto-open ranking for that league
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const lid = searchParams.get("league");
    if (!lid || !leagues.length) return;
    const target = leagues.find((l) => l.id === lid);
    if (target && (!rankingFor || rankingFor.id !== lid)) {
      viewRanking(target);
      // Clear the query param so navigating within the page doesn't re-trigger
      const next = new URLSearchParams(searchParams);
      next.delete("league");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, leagues]);

  const seedFemepa = async () => {
    if (!window.confirm("¿Crear las 21 ligas FEMEPA oficiales? (1ra-6ta + Open × Varonil/Femenil/Mixta). Es seguro: no duplica ligas existentes.")) return;
    try {
      const { data } = await api.post("/leagues/seed-femepa");
      toast.success(`${data.created_count} ligas FEMEPA creadas (${data.skipped_existing} ya existían)`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear ligas FEMEPA");
    }
  };

  const filteredLeagues = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = leagues;
    if (genderFilter !== "all") {
      out = out.filter((l) => (KIND_LABELS[l.kind]?.gender) === genderFilter);
    }
    if (q) {
      out = out.filter((l) =>
        (l.name || "").toLowerCase().includes(q) ||
        (l.description || "").toLowerCase().includes(q) ||
        (KIND_LABELS[l.kind]?.label || "").toLowerCase().includes(q)
      );
    }
    return out;
  }, [leagues, search, genderFilter]);

  const openCreate = () => { setForm(EMPTY); setDialog("create"); };
  const openEdit = (lg) => {
    const cids = Array.isArray(lg.club_ids) && lg.club_ids.length ? lg.club_ids : (lg.club_id ? [lg.club_id] : []);
    setForm({ name: lg.name, description: lg.description || "", kind: lg.kind, start_date: lg.start_date || "", end_date: lg.end_date || "", club_ids: cids });
    setDialog({ mode: "edit", league: lg });
  };

  const submit = async () => {
    try {
      const payload = { ...form, club_ids: form.club_ids || [], club_id: (form.club_ids || [])[0] || null };
      if (dialog === "create") await api.post("/leagues", payload);
      else await api.put(`/leagues/${dialog.league.id}`, payload);
      toast.success(dialog === "create" ? "Liga creada" : "Liga actualizada");
      setDialog(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const remove = async (lg) => {
    if (!window.confirm(`Eliminar "${lg.name}"? Los torneos quedarán sin liga.`)) return;
    try { await api.delete(`/leagues/${lg.id}`); toast.success("Eliminada"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const viewRanking = async (lg) => {
    setRankingFor(lg);
    setRankingData(null);
    setRankingCategory("all");
    try {
      const { data } = await api.get(`/leagues/${lg.id}/ranking-pairs`);
      setRankingData(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); setRankingFor(null); }
  };

  const reloadRanking = async (cat) => {
    if (!rankingFor) return;
    try {
      const q = cat && cat !== "all" ? `?category=${cat}` : "";
      const { data } = await api.get(`/leagues/${rankingFor.id}/ranking-pairs${q}`);
      setRankingData(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  // When the "Liguilla" bracket button is toggled, fetch the pair-ranking
  // which already contains the dynamic bracket (1 vs N, 2 vs N-1, …).
  useEffect(() => {
    if (!bracketFor) { setBracketData(null); return; }
    (async () => {
      try {
        const { data } = await api.get(`/leagues/${bracketFor.id}/ranking-pairs`);
        setBracketData(data);
      } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    })();
  }, [bracketFor]);

  // Inscripción: load tournaments of a league
  const openEnroll = async (lg) => {
    setEnrollFor(lg);
    setTournaments([]);
    try {
      const { data } = await api.get("/tournaments");
      setTournaments((data || []).filter((t) => t.league_id === lg.id));
    } catch { toast.error("Error al cargar torneos"); }
  };

  const enrollInTournament = async (tId) => {
    try {
      await api.post(`/tournaments/${tId}/enroll`);
      toast.success("Inscrito! Revisa la sección Torneos.");
      const { data } = await api.get("/tournaments");
      setTournaments((data || []).filter((t) => t.league_id === enrollFor.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al inscribir");
    }
  };

  // Marcadores: matches of all tournaments in this league (with date filter)
  const [marcadoresDate, setMarcadoresDate] = useState("");
  const [marcadoresMeta, setMarcadoresMeta] = useState(null);
  const openMarcadores = async (lg) => {
    setMarcadoresFor(lg);
    setMarcadoresData([]);
    setMarcadoresMeta(null);
    const today = new Date().toISOString().slice(0, 10);
    setMarcadoresDate(today);
    try {
      const { data } = await api.get(`/leagues/${lg.id}/marcadores`);
      setMarcadoresData(data.marcadores || []);
      setMarcadoresMeta({
        tournaments_count: data.tournaments_count,
        dates: data.dates || [],
      });
    } catch { toast.error("Error al cargar marcadores"); }
  };

  const marcadoresFilteredByDate = useMemo(() => {
    if (!marcadoresDate) return marcadoresData;
    return marcadoresData.filter((m) => (m.date || "").slice(0, 10) === marcadoresDate);
  }, [marcadoresData, marcadoresDate]);

  const marcadoresDates = useMemo(() => {
    if (marcadoresMeta?.dates && marcadoresMeta.dates.length > 0) {
      return marcadoresMeta.dates.map((d) => d.date);
    }
    const set = new Set(marcadoresData.map((m) => (m.date || "").slice(0, 10)).filter(Boolean));
    return Array.from(set).sort().reverse();
  }, [marcadoresData, marcadoresMeta]);

  // Tournament linking (admin)
  const openLink = async (lg) => {
    setLinkFor(lg);
    setLinkData(null);
    try {
      const { data } = await api.get(`/leagues/${lg.id}/available-tournaments`);
      setLinkData(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); setLinkFor(null); }
  };
  const linkTournament = async (tId) => {
    try {
      await api.put(`/leagues/${linkFor.id}/link-tournament/${tId}`);
      toast.success("Torneo vinculado");
      const { data } = await api.get(`/leagues/${linkFor.id}/available-tournaments`);
      setLinkData(data);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const unlinkTournament = async (tId) => {
    try {
      await api.delete(`/leagues/${linkFor.id}/link-tournament/${tId}`);
      toast.success("Torneo desvinculado");
      const { data } = await api.get(`/leagues/${linkFor.id}/available-tournaments`);
      setLinkData(data);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };


  return (
    <div className="space-y-6 animate-fade-in" data-testid="leagues-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">Competencias oficiales</p>
          <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black uppercase tracking-tighter">Ligas</h1>
          <p className="text-sm text-gray-400 mt-1">Ligas FEMEPA y propias. Cada liga muestra solo los partidos que se jugaron por esa liga (modo estricto).</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isAdmin && (
            <Button onClick={seedFemepa} variant="outline" className="border-[#CCFF00]/40 text-[#CCFF00] hover:bg-[#CCFF00]/10 font-bold uppercase tracking-wide text-xs" data-testid="seed-femepa-btn">
              <Trophy size={16} className="mr-2" /> Crear Ligas FEMEPA
            </Button>
          )}
          {canCreateLeagues && (
            <Button onClick={openCreate} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide text-xs" data-testid="create-league-btn">
              <Plus size={16} className="mr-2" /> Nueva liga
            </Button>
          )}
        </div>
      </div>

      {/* Filters: search + gender */}
      <div className="flex items-center gap-3 flex-wrap" data-testid="leagues-filters">
        <div className="relative flex-1 max-w-md min-w-[200px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar liga..."
            className="bg-[#0A0A0A] border-white/20 text-white pl-9 h-10 focus:border-[#CCFF00]"
            data-testid="leagues-search"
          />
        </div>
        <div className="flex items-center gap-1 flex-wrap" data-testid="gender-filter">
          {[
            { id: "all", label: "Todas", color: "bg-white/5 text-gray-300 border-white/10" },
            { id: "varonil", label: "Varonil", color: "bg-blue-500/10 text-blue-300 border-blue-500/30" },
            { id: "femenil", label: "Femenil", color: "bg-pink-500/10 text-pink-300 border-pink-500/30" },
            { id: "mixta", label: "Mixta", color: "bg-purple-500/10 text-purple-300 border-purple-500/30" },
          ].map((g) => (
            <button
              key={g.id}
              type="button"
              onClick={() => setGenderFilter(g.id)}
              className={`px-3 py-2 text-[10px] uppercase tracking-wider font-bold border transition-colors ${genderFilter === g.id ? `${g.color} ring-2 ring-[#CCFF00]/40` : "bg-[#0A0A0A] text-gray-400 border-white/10 hover:border-[#CCFF00]/30"}`}
              data-testid={`gender-filter-${g.id}`}
            >
              {g.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32"><div className="w-6 h-6 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>
      ) : filteredLeagues.length === 0 ? (
        <div className="card-tactical p-10 text-center">
          <Medal size={40} weight="duotone" className="text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400 text-sm">{search ? `Sin resultados para "${search}"` : (isAdmin ? 'Sin ligas aún. Crea "Liga Veteranos A", "Liga Veteranos B" o "Liga Sexta-Open" para comenzar.' : "Sin ligas disponibles aún.")}</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="leagues-grid">
          {filteredLeagues.map((lg) => {
            const info = KIND_LABELS[lg.kind] || KIND_LABELS.general;
            return (
              <div key={lg.id} className="card-tactical p-5 space-y-3" data-testid={`league-${lg.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-['Barlow_Condensed'] text-xl font-black uppercase text-white">{lg.name}</h3>
                    {lg.description && <p className="text-xs text-gray-500 mt-1">{lg.description}</p>}
                  </div>
                  <Badge variant="outline" className={info.color + " text-[10px]"}>{info.label}</Badge>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <Trophy size={14} className="text-[#CCFF00]" />
                  <span className="text-gray-400">Torneos:</span>
                  <span className="text-white font-bold font-['JetBrains_Mono']">{lg.tournaments_count || 0}</span>
                </div>
                <div className="flex gap-2 pt-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => viewRanking(lg)} className="border-[#CCFF00]/30 text-[#CCFF00] hover:bg-[#CCFF00]/10 text-xs uppercase h-8" data-testid={`league-ranking-${lg.id}`}><Medal size={14} className="mr-1" /> Ranking</Button>
                  <Link to={`/dashboard/marcador?league_id=${lg.id}`} className="inline-flex items-center border border-blue-500/30 text-blue-400 hover:bg-blue-500/10 text-xs uppercase h-8 px-3 font-semibold" data-testid={`league-marcadores-${lg.id}`}><Broadcast size={14} className="mr-1" /> Marcadores</Link>
                  {user?.role === "jugador" && (
                    <Button size="sm" onClick={() => openEnroll(lg)} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] text-xs uppercase font-bold h-8" data-testid={`league-enroll-${lg.id}`}><UserPlus size={14} className="mr-1" /> Inscribirme</Button>
                  )}
                  {isAdmin && (
                    <Button size="sm" variant="outline" onClick={() => openLink(lg)} className="border-[#FF6B00]/30 text-[#FF6B00] hover:bg-[#FF6B00]/10 text-xs uppercase h-8" data-testid={`league-link-${lg.id}`}><Trophy size={14} className="mr-1" /> Vincular</Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => setBracketFor(bracketFor?.id === lg.id ? null : lg)} className="border-[#7C5CFF]/30 text-[#7C5CFF] hover:bg-[#7C5CFF]/10 text-xs uppercase h-8" data-testid={`league-liguilla-${lg.id}`}><Trophy size={14} className="mr-1" /> Liguilla</Button>
                  <Button size="sm" variant="outline" onClick={() => navigate(`/dashboard/ligas/${lg.id}/grupos`)} className="border-[#1E40AF]/40 text-[#93C5FD] hover:bg-[#1E40AF]/10 text-xs uppercase h-8" data-testid={`league-groups-${lg.id}`}><Users size={14} className="mr-1" /> Grupos</Button>
                  {canManageLeague(lg) && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => openEdit(lg)} className="border-white/10 text-xs uppercase h-8" data-testid={`league-edit-${lg.id}`}><PencilSimple size={14} className="mr-1" /> Editar</Button>
                      <Button size="sm" variant="outline" onClick={() => remove(lg)} className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs uppercase h-8" data-testid={`league-delete-${lg.id}`}><Trash size={14} /></Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Enroll dialog: list tournaments of a league + enroll button */}
      <Dialog open={!!enrollFor} onOpenChange={(v) => !v && setEnrollFor(null)}>
        <DialogContent className="bg-[#141414] border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="league-enroll-dialog">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase">{enrollFor?.name} · Inscripción</DialogTitle></DialogHeader>
          <p className="text-xs text-gray-400 mb-3">Selecciona un torneo abierto para inscribirte. Tus inscripciones aparecen en la sección "Torneos".</p>
          {tournaments.length === 0 ? (
            <p className="text-sm text-gray-500">Esta liga no tiene torneos activos. Vuelve más tarde.</p>
          ) : (
            <div className="space-y-2">
              {tournaments.map((t) => {
                const enrolled = (t.players || []).includes(user?.id);
                const full = (t.players?.length || 0) >= (t.max_players || 999);
                const closed = t.status !== "abierto";
                return (
                  <div key={t.id} className="bg-[#0A0A0A] border border-white/10 p-3 flex items-center justify-between gap-2 flex-wrap" data-testid={`enroll-tour-${t.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white font-semibold truncate">{t.name}</p>
                      <div className="flex gap-2 mt-1 flex-wrap text-[10px] uppercase tracking-widest text-gray-500">
                        <span>{t.start_date}</span>
                        <span>{t.players?.length || 0}/{t.max_players}</span>
                        {t.category && <Badge variant="outline" className="bg-[#CCFF00]/10 text-[#CCFF00] border-[#CCFF00]/30 text-[9px] py-0">Cat. {t.category}</Badge>}
                        <Badge variant="outline" className="bg-white/5 text-gray-400 border-white/10 text-[9px] py-0">{t.status}</Badge>
                      </div>
                    </div>
                    {enrolled ? (
                      <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/30 text-[10px]"><CheckCircle size={12} className="mr-1" /> Inscrito</Badge>
                    ) : (
                      <Button size="sm" onClick={() => enrollInTournament(t.id)} disabled={full || closed}
                        className="bg-[#CCFF00] text-black hover:bg-[#B3E600] disabled:bg-gray-700 disabled:text-gray-500 uppercase text-[10px] font-bold h-8"
                        data-testid={`enroll-btn-${t.id}`}>
                        {full ? "Lleno" : closed ? "Cerrado" : "Inscribirme"}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <Link to="/dashboard/torneos" className="text-xs text-[#CCFF00] hover:underline uppercase tracking-wider mt-3 inline-block">Ver todos los torneos →</Link>
        </DialogContent>
      </Dialog>

      {/* Marcadores dialog: validated matches of league tournaments, filtered by date */}
      <Dialog open={!!marcadoresFor} onOpenChange={(v) => !v && setMarcadoresFor(null)}>
        <DialogContent className="bg-[#141414] border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="league-marcadores-dialog">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase">{marcadoresFor?.name} · Marcadores</DialogTitle></DialogHeader>

          {marcadoresMeta && marcadoresMeta.tournaments_count === 0 && marcadoresData.length === 0 && (
            <div className="bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[11px] p-3 mb-3" data-testid="marcadores-empty-notice">
              ⚠️ Esta liga aún no tiene torneos vinculados ni partidos registrados directamente. {isAdmin ? "Usa el botón \"Vincular\" en la liga para asociar torneos existentes." : "Pídele al admin que vincule torneos a esta liga."}
            </div>
          )}

          {/* Date filter */}
          <div className="bg-[#0A0A0A] border border-white/10 p-3 mb-3 space-y-2" data-testid="marcadores-date-filter">
            <div className="flex items-center gap-2 flex-wrap">
              <Label className="text-[10px] uppercase tracking-widest text-[#CCFF00] font-semibold">Día seleccionado:</Label>
              <Input
                type="date"
                value={marcadoresDate}
                onChange={(e) => setMarcadoresDate(e.target.value)}
                className="bg-[#141414] border-white/20 text-white h-8 text-xs w-40 focus:border-[#CCFF00]"
                data-testid="marcadores-date-input"
              />
              {marcadoresDate && (
                <Button size="sm" variant="outline" onClick={() => setMarcadoresDate("")} className="border-white/20 text-gray-400 hover:text-white text-[10px] h-8 uppercase" data-testid="marcadores-clear-date">
                  Ver todos
                </Button>
              )}
            </div>
            {marcadoresDates.length > 0 && (
              <div className="flex items-center gap-1 flex-wrap pt-1">
                <span className="text-[9px] uppercase tracking-widest text-gray-600 mr-1">Días con partidos:</span>
                {marcadoresDates.slice(0, 8).map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setMarcadoresDate(d)}
                    className={`px-2 py-1 text-[10px] font-['JetBrains_Mono'] tracking-wider border transition-colors ${marcadoresDate === d ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-[#141414] text-gray-400 border-white/10 hover:border-[#CCFF00]/40"}`}
                    data-testid={`marcadores-quick-date-${d}`}
                  >
                    {d.slice(5)}
                  </button>
                ))}
              </div>
            )}
          </div>

          {marcadoresData.length === 0 ? (
            <p className="text-sm text-gray-500">Sin marcadores validados en esta liga aún.</p>
          ) : marcadoresFilteredByDate.length === 0 ? (
            <p className="text-sm text-gray-500" data-testid="marcadores-empty-day">
              {marcadoresDate ? `Sin partidos validados el ${marcadoresDate} en esta liga.` : "Sin resultados."}
            </p>
          ) : (
            <div className="space-y-1" data-testid="marcadores-list">
              <p className="text-[10px] text-gray-500 uppercase tracking-widest mb-2">
                {marcadoresFilteredByDate.length} partido{marcadoresFilteredByDate.length !== 1 ? "s" : ""} {marcadoresDate ? `el ${marcadoresDate}` : "en total"}
              </p>
              {marcadoresFilteredByDate.map((m) => {
                const s1 = m.sets.filter((s) => s.winner === 1).length;
                const s2 = m.sets.filter((s) => s.winner === 2).length;
                return (
                  <Link to={`/dashboard/marcador/${m.id}`} key={m.id}
                    className="flex items-center justify-between gap-3 py-2 px-2 border-b border-white/5 hover:bg-white/5 transition-colors"
                    data-testid={`league-marcador-${m.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="bg-blue-500/10 text-blue-400 border-blue-500/30 text-[9px]">VALIDADO</Badge>
                        <span className="text-[10px] text-gray-600 font-['JetBrains_Mono']">{m.date}</span>
                        {m._tournament_name && <span className="text-[10px] text-gray-500 truncate">{m._tournament_name}</span>}
                      </div>
                      <p className="text-sm text-white truncate mt-0.5">
                        <span className={m.winner_pair === 1 ? "text-[#CCFF00] font-semibold" : ""}>{m.pair1.p1_name} / {m.pair1.p2_name}</span>
                        <span className="text-gray-600"> vs </span>
                        <span className={m.winner_pair === 2 ? "text-[#CCFF00] font-semibold" : ""}>{m.pair2.p1_name} / {m.pair2.p2_name}</span>
                      </p>
                    </div>
                    <div className="font-['Barlow_Condensed'] font-black text-xl text-white flex items-center gap-1 shrink-0">
                      <span className={m.winner_pair === 1 ? "text-[#CCFF00]" : ""}>{s1}</span>
                      <span className="text-gray-600">-</span>
                      <span className={m.winner_pair === 2 ? "text-[#CCFF00]" : ""}>{s2}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create/Edit Dialog (admin only) */}
      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="bg-[#141414] border-white/10 max-w-md">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase">{dialog === "create" ? "Nueva liga" : "Editar liga"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase">Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0A] border-white/10" data-testid="league-form-name" /></div>
            <div>
              <Label className="text-xs uppercase">Tipo</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/10" data-testid="league-form-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="veteranos_a">Veteranos A</SelectItem>
                  <SelectItem value="veteranos_b">Veteranos B</SelectItem>
                  <SelectItem value="sexta_open">Sexta-Open</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs uppercase">Descripcion</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-[#0A0A0A] border-white/10" rows={3} /></div>
            <div>
              <Label className="text-xs uppercase mb-2 block">Club(es) sede (opcional)</Label>
              <MultiClubSelect
                clubs={clubs}
                value={form.club_ids || []}
                onChange={(ids) => setForm({ ...form, club_ids: ids })}
                testid="league-form-club-multi"
                emptyLabel="Liga sin clubes específicos (abierta)"
              />
              <p className="text-[10px] text-gray-500 mt-1">Si eliges clubes, sólo los directivos de esos clubes podrán editar la liga (junto al admin).</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase">Fecha inicio</Label>
                <Input type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="bg-[#0A0A0A] border-white/10" data-testid="league-form-start-date" />
              </div>
              <div>
                <Label className="text-xs uppercase">Fecha fin</Label>
                <Input type="date" value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="bg-[#0A0A0A] border-white/10" data-testid="league-form-end-date" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialog(null)} className="border-white/10">Cancelar</Button>
              <Button onClick={submit} disabled={!form.name.trim()} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="league-form-submit">Guardar</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ranking Dialog */}
      <Dialog open={!!rankingFor} onOpenChange={(v) => !v && setRankingFor(null)}>
        <DialogContent className="bg-[#141414] border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="league-ranking-dialog">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase">{rankingFor?.name} · Ranking</DialogTitle></DialogHeader>
          <div className="mb-3 flex items-center gap-2 flex-wrap">
            <Label className="text-[10px] uppercase tracking-widest text-[#CCFF00] font-semibold">Filtrar por categoría:</Label>
            <Select value={rankingCategory} onValueChange={(v) => { setRankingCategory(v); reloadRanking(v); }}>
              <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-8 text-xs w-40" data-testid="league-ranking-cat">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-white/10">
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="6">6ta</SelectItem>
                <SelectItem value="5">5ta</SelectItem>
                <SelectItem value="4">4ta</SelectItem>
                <SelectItem value="3">3ra</SelectItem>
                <SelectItem value="2">2da</SelectItem>
                <SelectItem value="1">1ra</SelectItem>
                <SelectItem value="open">Open</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {!rankingData ? (
            <div className="flex items-center justify-center h-32"><div className="w-6 h-6 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>
          ) : (
            <div className="space-y-3" data-testid="league-ranking-full">
              {rankingData.tournaments_count === 0 && rankingData.ranking.length === 0 && (
                <div className="bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[11px] p-3" data-testid="ranking-empty-notice">
                  ⚠️ Esta liga aún no tiene torneos vinculados ni partidos. {isAdmin ? "Vincula torneos con el botón \"Vincular\" o crea partidos asociados a esta liga." : "Pídele al admin que vincule torneos a esta liga."}
                </div>
              )}
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-xs text-gray-400">
                  Basado en {rankingData.tournaments_count} torneo(s){rankingCategory !== "all" ? ` · Cat. ${rankingCategory}` : ""}
                </p>
                <Badge variant="outline" className="bg-[#CCFF00]/10 text-[#CCFF00] border-[#CCFF00]/30 text-[10px] tracking-widest">
                  <Trophy size={12} className="mr-1" weight="fill" /> Listado completo ({rankingData.ranking.length})
                </Badge>
              </div>
              {rankingData.ranking.length === 0 ? (
                <p className="text-xs text-gray-500">{rankingCategory !== "all" ? `Sin duplas de categoría ${rankingCategory} con partidos validados en esta liga.` : "Sin partidos validados en esta liga aún"}</p>
              ) : (
                <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1" data-testid="ranking-full-scroll">
                  {rankingData.ranking.map((r, i) => {
                    const podium = i === 0 ? { c: "#FFD700", bg: "rgba(255,215,0,0.08)", bd: "rgba(255,215,0,0.3)", lbl: "🥇" }
                      : i === 1 ? { c: "#C0C0C0", bg: "rgba(192,192,192,0.06)", bd: "rgba(192,192,192,0.25)", lbl: "🥈" }
                      : i === 2 ? { c: "#CD7F32", bg: "rgba(205,127,50,0.06)", bd: "rgba(205,127,50,0.25)", lbl: "🥉" }
                      : null;
                    const winRate = r.matches > 0 ? Math.round((r.wins / r.matches) * 100) : 0;
                    return (
                      <div
                        key={r.pair_key || r.display_name + i}
                        className="flex items-center gap-3 p-3 border"
                        style={{
                          backgroundColor: podium?.bg || "#0A0A0A",
                          borderColor: podium?.bd || "rgba(255,255,255,0.1)",
                        }}
                        data-testid={`league-rank-${i}`}
                      >
                        <span
                          className="font-['Barlow_Condensed'] text-2xl font-black w-10 text-center shrink-0"
                          style={{ color: podium?.c || "#4B5563" }}
                        >
                          {podium ? podium.lbl : `#${i + 1}`}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 text-sm font-semibold" style={{ color: podium?.c || "#FFFFFF" }} data-testid={`league-rank-names-${i}`}>
                            <span className="truncate">{r.p1_name || (r.display_name || "").split(" / ")[0]}</span>
                            <span className="text-[#CCFF00] font-black shrink-0" aria-hidden="true">·</span>
                            <span className="truncate">{r.p2_name || (r.display_name || "").split(" / ")[1] || ""}</span>
                          </div>
                          <p className="text-[10px] text-gray-500 uppercase tracking-widest font-['JetBrains_Mono']">
                            Dupla · {r.matches}p · {r.wins}v · {winRate}%
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="font-['Barlow_Condensed'] font-black text-2xl leading-none" style={{ color: podium?.c || "#CCFF00" }}>{r.points}</p>
                          <p className="text-[9px] text-gray-500 uppercase tracking-widest">pts</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {user?.id && (
                <div className="mt-4" data-testid="league-user-best-pairs">
                  <BestPairs
                    userId={user.id}
                    userName={user.name}
                    defaultPeriod="week"
                    compact
                    testid="league-best-pairs"
                  />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Liguilla bracket dialog */}
      <Dialog open={!!bracketFor} onOpenChange={(v) => !v && setBracketFor(null)}>
        <DialogContent className="bg-[#141414] border-white/10 max-w-6xl max-h-[85vh] overflow-y-auto" data-testid="league-bracket-dialog">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase">{bracketFor?.name} · Liguilla</DialogTitle></DialogHeader>
          {!bracketData ? (
            <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>
          ) : (() => {
            const size = bracketData.bracket_size || 0;
            const firstRound = bracketData.bracket_pairs || [];
            const roundLabel = (n) => {
              if (n === 32) return "16avos";
              if (n === 16) return "Octavos";
              if (n === 8) return "Cuartos";
              if (n === 4) return "Semifinales";
              if (n === 2) return "Gran Final";
              return `Ronda de ${n}`;
            };
            const pairName = (p) => p?.display_name || (p ? `${p.p1_name || "—"} · ${p.p2_name || "—"}` : "—");
            const seedBox = (p, seed) => (
              <div className="flex items-center gap-2 p-2 bg-black/40 border border-white/10 rounded min-h-[46px]" data-testid={`liguilla-seed-${seed}`}>
                <span className="text-[9px] tracking-widest uppercase text-[#CCFF00] font-bold w-6">{seed}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-white flex items-center gap-1">
                    <span className="truncate">{p?.p1_name || "—"}</span>
                    <span className="text-[#CCFF00] font-black">·</span>
                    <span className="truncate">{p?.p2_name || "—"}</span>
                  </div>
                  <p className="text-[9px] uppercase text-gray-500">{p?.points != null ? `${p.points} pts` : ""}</p>
                </div>
              </div>
            );
            const emptyMatch = (label) => (
              <div className="flex flex-col items-center justify-center p-2 bg-[#CCFF00]/5 border border-[#CCFF00]/20 rounded min-h-[96px]">
                <span className="text-[9px] tracking-widest uppercase text-[#CCFF00] font-bold mb-1">{label}</span>
                <div className="w-full h-5 border border-white/10 rounded bg-black/20" />
                <span className="text-[10px] text-gray-500 my-1">VS</span>
                <div className="w-full h-5 border border-white/10 rounded bg-black/20" />
              </div>
            );

            if (size === 0) {
              return (
                <div className="p-6 text-center bg-amber-500/5 border border-amber-500/20 rounded" data-testid="league-liguilla-empty">
                  <p className="text-sm text-amber-300">Se necesitan al menos <b>2 duplas</b> con partidos validados en la liga para armar la liguilla.</p>
                  <p className="text-[11px] text-gray-500 mt-1">Duplas actuales: {bracketData.ranking?.length || 0}</p>
                </div>
              );
            }

            // Build column list dynamically: first round from backend, then empty match placeholders,
            // ending in "Campeón".
            const columns = [];
            columns.push({
              label: roundLabel(size),
              nodes: firstRound.map((p, i) => (
                <div key={i} className="space-y-1" data-testid={`liguilla-r0-${i}`}>
                  {seedBox(p.top, p.seed_high)}
                  {seedBox(p.bottom, p.seed_low)}
                </div>
              )),
            });
            let matchesInRound = firstRound.length / 2;
            let cur = size / 2;
            while (matchesInRound >= 1) {
              const label = roundLabel(cur);
              const arr = [];
              for (let i = 0; i < matchesInRound; i++) {
                arr.push(<div key={i}>{emptyMatch(`${label} ${matchesInRound > 1 ? i + 1 : ""}`.trim())}</div>);
              }
              columns.push({ label, nodes: arr });
              matchesInRound = Math.floor(matchesInRound / 2);
              cur = cur / 2;
            }
            columns.push({
              label: "Campeón",
              nodes: [
                <div key="champ" className="p-4 bg-gradient-to-br from-[#CCFF00]/20 to-[#CCFF00]/5 border border-[#CCFF00]/40 rounded text-center">
                  <p className="text-[10px] uppercase text-gray-500">Por definir</p>
                  <p className="text-white font-['Barlow_Condensed'] text-lg font-black mt-1">🏆</p>
                </div>,
              ],
            });

            return (
              <>
                <p className="text-[11px] text-gray-500 mb-3">
                  Cuadro dinámico armado con el TOP <b>{size}</b> del ranking por dupla ·
                  {" "}<span className="text-[#CCFF00]">1 vs {size}, 2 vs {size - 1}, 3 vs {size - 2}…</span>
                  {" "}({bracketData.ranking?.length || 0} duplas registradas)
                </p>
                <div
                  className="grid gap-4 overflow-x-auto"
                  style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(160px, 1fr))` }}
                  data-testid="league-liguilla-grid"
                >
                  {columns.map((col, idx) => (
                    <div key={idx} className="space-y-3 flex flex-col justify-around" data-testid={`liguilla-col-${idx}`}>
                      <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#CCFF00] text-center">{col.label}</p>
                      {col.nodes}
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Tournament linking Dialog (admin) */}
      <Dialog open={!!linkFor} onOpenChange={(v) => !v && setLinkFor(null)}>
        <DialogContent className="bg-[#141414] border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="league-link-dialog">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase">{linkFor?.name} · Vincular Torneos</DialogTitle></DialogHeader>
          <p className="text-[11px] text-gray-500 mb-4">Vincula torneos existentes a esta liga. Los partidos validados de los torneos vinculados aparecerán en el ranking y marcadores.</p>
          {!linkData ? (
            <div className="flex items-center justify-center h-32"><div className="w-6 h-6 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>
          ) : (
            <div className="space-y-4">
              <div>
                <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-2">Torneos vinculados ({linkData.linked.length})</p>
                {linkData.linked.length === 0 ? (
                  <p className="text-[11px] text-gray-500 italic">Aún no hay torneos vinculados.</p>
                ) : (
                  <div className="space-y-1">
                    {linkData.linked.map((t) => (
                      <div key={t.id} className="flex items-center gap-2 p-2 bg-[#CCFF00]/5 border border-[#CCFF00]/20" data-testid={`linked-tour-${t.id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white truncate">{t.name}</p>
                          <p className="text-[9px] uppercase tracking-widest text-gray-500">{t.start_date || "?"} · {t.status}{t.category ? ` · Cat. ${t.category}` : ""}</p>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => unlinkTournament(t.id)} className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-[10px] uppercase h-7" data-testid={`unlink-${t.id}`}>Desvincular</Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <p className="text-xs tracking-[0.2em] uppercase font-semibold text-gray-400 mb-2">Disponibles ({linkData.unlinked.length})</p>
                {linkData.unlinked.length === 0 ? (
                  <p className="text-[11px] text-gray-500 italic">No hay torneos disponibles sin vincular.</p>
                ) : (
                  <div className="space-y-1">
                    {linkData.unlinked.map((t) => (
                      <div key={t.id} className="flex items-center gap-2 p-2 bg-[#0A0A0A] border border-white/10 hover:border-[#CCFF00]/30 transition-colors" data-testid={`unlinked-tour-${t.id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white truncate">{t.name}</p>
                          <p className="text-[9px] uppercase tracking-widest text-gray-500">{t.start_date || "?"} · {t.status}{t.category ? ` · Cat. ${t.category}` : ""}</p>
                        </div>
                        <Button size="sm" onClick={() => linkTournament(t.id)} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] text-[10px] uppercase font-bold h-7" data-testid={`link-${t.id}`}>Vincular</Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
